import { apiClient } from '@/shared/api/apiClient'
import { useMutation } from '@tanstack/react-query'

export type RadarReviewTextItem = {
  source: 'celex2scope'
  celex: string
  title: string | null
  scope: string | null
  rank: number
  lexicalScore: number
  vectorScore: number
  combinedScore: number
}

export type RadarReviewTextResponse = {
  queryText: string
  retrievalMinScore: number
  relevanceThreshold: number
  metadata: {
    ftsConfig: 'english'
    lexicalWeight: number
    vectorWeight: number
    minCombinedScore: number
    limit: number
    queryLength: number
    embeddingModel: string
    vectorQueryText: string
    lexicalQueryText: string
    // Keep this explicit wire-contract mirror in client to avoid coupling to server internals.
    lexicalQueryMode:
      | 'plainto_and'
      | 'websearch_phrase_or'
      | 'websearch_passthrough'
    lexicalQueryParser: 'plainto' | 'websearch'
    lexicalContentTermCount: number
    keywordSource: 'llm' | 'none'
    extractedKeywords: string[]
    lexicalHeadTerm: string | null
    lexicalPhrases: string[]
  }
  counts: {
    full: number
    relevant: number
  }
  items: RadarReviewTextItem[]
  automotiveCoverage?: {
    total: number
    retrieved: number
    missingCelexes: string[]
  }
  automotiveDiagnostics?: {
    confusionMatrix: {
      counts: {
        tp: number
        fp: number
        fn: number
        tn: number
      }
      rates: {
        precision: number | null
        recall: number | null
        f1: number | null
      }
    }
    thresholdMatrix: Array<{
      retrievalFloor: number
      relevanceThreshold: number
      counts: {
        tp: number
        fp: number
        fn: number
        tn: number
      }
      rates: {
        precision: number | null
        recall: number | null
        f1: number | null
      }
    }>
    weightSensitivity: Array<{
      lexicalWeight: number
      vectorWeight: number
      counts: {
        tp: number
        fp: number
        fn: number
        tn: number
      }
      rates: {
        precision: number | null
        recall: number | null
        f1: number | null
      }
    }>
  }
}

export type RadarReviewCnCodeItem = {
  celex: string
  title: string | null
  scope: string | null
}

export type RadarReviewCnCodeResponse = {
  cncode: string
  count: number
  items: RadarReviewCnCodeItem[]
}

export function useSearchRadarReviewText() {
  return useMutation({
    mutationFn: async (input: {
      queryText: string
      relevanceThreshold?: number
      minCombinedScore?: number
      limit?: number
      lexicalWeight?: number
      vectorWeight?: number
      automotiveDiagnostics?: boolean
    }) => {
      const response = await apiClient.get<RadarReviewTextResponse>(
        '/radar/review/celex-scopes',
        {
          params: {
            q: input.queryText,
            relevanceThreshold: input.relevanceThreshold,
            minCombinedScore: input.minCombinedScore,
            limit: input.limit,
            lexicalWeight: input.lexicalWeight,
            vectorWeight: input.vectorWeight,
            automotiveDiagnostics: input.automotiveDiagnostics,
          },
        }
      )
      return response.data
    },
  })
}

export function useSearchRadarReviewCnCode() {
  return useMutation({
    mutationFn: async (input: { cncode: string }) => {
      const response = await apiClient.get<RadarReviewCnCodeResponse>(
        '/radar/review/cn-code',
        {
          params: {
            cncode: input.cncode,
          },
        }
      )
      return response.data
    },
  })
}
